import React, { useState } from "react"
import PrimaryButton from "../../../../Ui/Buttons/PrimaryButton"
import { InputOrderRefundFee, OrderRefund } from "@lib/types/generated/graphql-types"
import { useMutation } from "@apollo/client"
import UPDATE_REFUND from "../../../../../graphql/mutations/order/refunds/UpdateRefund"
import { ReactComponent as TrashIcon } from "../../../../../images/icons/trash-can.svg"
import { Loader } from "../../../../Ui/Loader"
import { theme } from "../../../../../theme"
import {
  AddedReason,
  Container,
  Label,
  LabelWrapper,
  StyledSelect,
  TaxWrapper,
  Wrapper
} from "./RefundFee.styled"
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form"
import Input from "../../../../Ui/Form/Input"
import { ReactComponent as DownIcon } from "images/icons/chevron-down.svg"
import Money from "../../../../Money"
import DineroFactory from "dinero.js"
import { getRefundInput } from "../Helpers/helpers"
import { handleErrorMessages } from "helpers/errors"

type Props = {
  refund: OrderRefund
  refetch: () => void
  currencyCode: string
}

const options = [
  {
    value: "2",
    label: "2"
  },
  {
    value: "3",
    label: "3"
  }
]

const RefundFee = ({ refund, refetch, currencyCode }: Props) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const {
    getValues,
    control,
    setValue,
    handleSubmit,
    formState: { isValid }
  } = useForm<InputOrderRefundFee>({
    mode: "onBlur"
  })

  const [updateRefund, { loading }] = useMutation(UPDATE_REFUND, {
    onCompleted: () => {
      refetch()
      setExpanded(false)
      setValue("name", "")
      setValue("amount", 0)
      setValue("taxPercentage", 0)
      setValue("taxPercentageDecimals", 0)
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const updateRefundFee = (refundFee: InputOrderRefundFee) => {
    updateRefund({
      variables: {
        refundId: refund.id,
        input: {
          ...getRefundInput(refund),
          fee: { ...refundFee }
        }
      }
    })
  }

  const removeRefundFee = () => {
    updateRefund({
      variables: {
        refundId: refund.id,
        input: {
          ...getRefundInput(refund),
          fee: null
        }
      }
    })
  }

  const onSubmit: SubmitHandler<InputOrderRefundFee> = (data) => {
    updateRefundFee(data)
  }

  const form = useWatch({
    control
  })

  if (refund?.fee)
    return (
      <Wrapper>
        <Label>Refund Fee</Label>
        <AddedReason>
          <div>
            <span>
              <b>Name:</b> {refund?.fee.name}
            </span>
            <span>
              <b>Amount:</b>{" "}
              <Money
                amount={refund?.fee.amount}
                currencyUnit={currencyCode as DineroFactory.Currency}
              />
            </span>
          </div>
          {loading ? (
            <Loader color={theme.colors.grey} />
          ) : (
            <TrashIcon onClick={removeRefundFee}></TrashIcon>
          )}
        </AddedReason>
      </Wrapper>
    )

  return (
    <Wrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LabelWrapper onClick={() => setExpanded(!expanded)}>
          <Label>Refund fee</Label>
          <DownIcon />
        </LabelWrapper>
        {expanded && (
          <Container>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <Input
                  label="Amount in minor units"
                  removePaddingBottom={true}
                  type={"number"}
                  {...field}
                  onChange={(value: React.FormEvent<HTMLInputElement>) =>
                    field.onChange(value.currentTarget.valueAsNumber)
                  }
                />
              )}
              rules={{ required: true, min: 1 }}
            />
            <div>
              {" "}
              <strong>Amount formatted: </strong>
              <Money
                amount={getValues("amount") || 0}
                currencyUnit={currencyCode as DineroFactory.Currency}
              />
            </div>
            <Controller
              name="name"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field }) => <Input label="Name" removePaddingBottom={true} {...field} />}
            />
            <TaxWrapper>
              <Controller
                name="taxPercentage"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Tax percentage"
                    removePaddingBottom={true}
                    type={"number"}
                    {...field}
                    onChange={(value: React.FormEvent<HTMLInputElement>) =>
                      field.onChange(value.currentTarget.valueAsNumber)
                    }
                  />
                )}
                rules={{ required: true, min: 1 }}
              />
              <StyledSelect
                options={options}
                label="Tax percentage decimals"
                setValue={(e) => setValue("taxPercentageDecimals", parseInt(e))}
              />
            </TaxWrapper>
            <div>
              <strong>Tax formatted: </strong>
              {form.taxPercentage && form?.taxPercentageDecimals
                ? getValues("taxPercentage") / 10 ** getValues("taxPercentageDecimals") + "%"
                : "0"}
            </div>
            <PrimaryButton
              loading={loading}
              type="submit"
              disabled={
                !isValid ||
                !getValues("taxPercentageDecimals") ||
                !getValues("amount") ||
                !getValues("taxPercentage")
              }
            >
              Add refund fee
            </PrimaryButton>
          </Container>
        )}
      </form>
    </Wrapper>
  )
}

export default RefundFee
